import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../utils/api'; // your existing axios wrapper

interface Step1Data {
  timeline: string;
  district: string;
  constituency: string;
  ward: string;
  votingCenter: string;
  observerName: string;
  observerContact: string;
  observerOrganization: string;
}

interface Step1Props {
  data: Step1Data;
  onChange: (data: Step1Data) => void;
  errors: Record<string, string>;
  stepNumber?: number;
  currentSection?: number;
  totalSections?: number;
}

type PollingCenter = { id: number; name: string; ward_id?: number };

const Step1LocationInfo: React.FC<Step1Props> = ({
  data,
  onChange,
  errors,
  stepNumber = 1,
  currentSection = 1,
  totalSections = 11
}) => {
  const { user } = useAuth();

  // Polling centers lookup
  const [centers, setCenters] = useState<PollingCenter[]>([]);
  const [centersLoading, setCentersLoading] = useState(false);
  const [centersErr, setCentersErr] = useState<string | null>(null);

  // Hydrate read-only fields from authenticated user
  useEffect(() => {
    if (!user) {
      // User logged out - clear all data completely
      onChange({
        timeline: '', // Also reset timeline on logout
        district: '',
        constituency: '',
        ward: '',
        votingCenter: '',
        observerName: '',
        observerContact: '',
        observerOrganization: '',
      });
      return;
    }

    const newDistrict = user?.district ?? '';
    const newConstituency = user?.constituency ?? '';
    const newWard = user?.ward ?? '';
    const newObserverName = user?.name ?? '';
    const newObserverContact = user?.phone ?? '';
    const newObserverOrganization = user?.organisation_name ??
      user?.organization_name ??
      user?.organisation?.name ?? '';

    // Only update if the user data has actually changed to prevent loops
    if (data.district !== newDistrict ||
      data.constituency !== newConstituency ||
      data.ward !== newWard ||
      data.observerName !== newObserverName ||
      data.observerContact !== newObserverContact ||
      data.observerOrganization !== newObserverOrganization) {

      onChange({
        timeline: data.timeline, // Preserve timeline selection
        district: newDistrict,
        constituency: newConstituency,
        ward: newWard,
        votingCenter: '', // Reset voting center when user changes
        observerName: newObserverName,
        observerContact: newObserverContact,
        observerOrganization: newObserverOrganization,
      });
    }
  }, [user, data.district, data.constituency, data.ward, data.observerName, data.observerContact, data.observerOrganization, data.timeline, onChange]);

  // Fetch polling centers via /dashboard/overview lookups
  useEffect(() => {
    const d = (data.district || '').trim();
    const c = (data.constituency || '').trim();
    const w = (data.ward || '').trim();

    if (!d || !c || !w) {
      setCenters([]);
      return;
    }

    let cancelled = false;
    setCentersLoading(true);
    setCentersErr(null);

    (async () => {
      try {
        const resp = await api.dashboard.getOverview({
          district: d,
          constituency: c,
          ward: w,
          include_all_polling: '1',
        });

        const lookups = resp?.data?.data?.lookups;
        const pollingcenters: PollingCenter[] = lookups?.pollingcenters ?? [];
        const sorted = [...pollingcenters].sort((a, b) =>
          String(a.name).localeCompare(String(b.name))
        );

        if (!cancelled) setCenters(sorted);
      } catch (err: any) {
        if (!cancelled) {
          setCenters([]);
          setCentersErr(err?.message || 'Failed to fetch polling centers');
        }
      } finally {
        if (!cancelled) setCentersLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [data.district, data.constituency, data.ward]);

  // Parent update helper
  const handleInputChange = (field: keyof Step1Data, value: string) => {
    onChange({ ...data, [field]: value });
  };

  // All timelines are always available - no time-based restrictions
  const getTimeBasedRestrictions = () => {
    const restrictions = {
      morning: { enabled: true as boolean, reason: '' as string },
      afternoon: { enabled: true as boolean, reason: '' as string },
      vote_count: { enabled: true as boolean, reason: '' as string }
    };

    return restrictions;
  };

  const timeRestrictions = getTimeBasedRestrictions();

  const timelineOptions = [
    {
      value: 'morning',
      label: 'Morning (6:00 AM to 12:00 PM)',
      enabled: timeRestrictions.morning.enabled,
      reason: timeRestrictions.morning.reason
    },
    {
      value: 'afternoon',
      label: 'Afternoon (12:00 PM to 4:00 PM)',
      enabled: timeRestrictions.afternoon.enabled,
      reason: timeRestrictions.afternoon.reason
    },
    {
      value: 'vote_count',
      label: 'Vote Count (4:00 PM to 6:00 AM)',
      enabled: timeRestrictions.vote_count.enabled,
      reason: timeRestrictions.vote_count.reason
    },
  ];

  // Keep a pre-filled center visible even if it’s not in the fetched list
  const hasCurrentCenter = useMemo(
    () => !!centers.find((c) => c.name === data.votingCenter),
    [centers, data.votingCenter]
  );

  // Step metadata
  const stepMetadata = {
    stepNumber: stepNumber,
    title: "Location & Basic Information",
    description: "Please provide the observation location details and observer information.",
    totalQuestions: 8, // District, Constituency, Ward, Voting Center, Timeline, Observer Name, Contact, Organization
    currentSection: currentSection,
    totalSections: totalSections,
  };

  return (
    <div className="space-y-6">
      {/* Section Header with Step Number and Progress */}
      <div className="p-4 border border-blue-100 rounded-lg bg-blue-50">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              Step {stepMetadata.stepNumber}: {stepMetadata.title}
            </h2>
            <p className="mt-1 text-sm text-gray-600">
              {stepMetadata.description}
            </p>
          </div>
          <div className="text-right">
            <span className="px-3 py-1 text-sm font-medium text-blue-800 bg-blue-100 rounded-full">
              Section {stepMetadata.currentSection}/{stepMetadata.totalSections}
            </span>
          </div>
        </div>
        <div className="mt-3 h-1.5 w-full bg-gray-200 rounded-full overflow-hidden">
          <div
            className="h-full bg-blue-600 rounded-full"
            style={{ width: `${(stepMetadata.currentSection / stepMetadata.totalSections) * 100}%` }}
          ></div>
        </div>
      </div>

      {/* Timeline */}
      <div>
        <label className="label">
          Observation Timeline <span className="text-red-500">*</span>
        </label>
        <select
          className={`input ${errors.timeline ? 'border-red-500' : ''}`}
          value={data.timeline}
          onChange={(e) => handleInputChange('timeline', e.target.value)}
        >
          <option value="">Select Observation Timeline</option>
          {timelineOptions.map((opt) => (
            <option
              key={opt.value}
              value={opt.value}
              disabled={!opt.enabled}
              style={{
                color: opt.enabled ? 'inherit' : '#9ca3af',
                backgroundColor: opt.enabled ? 'inherit' : '#f3f4f6'
              }}
            >
              {opt.label} {!opt.enabled && '🔒'}
            </option>
          ))}
        </select>
        {errors.timeline && <p className="mt-1 text-sm text-red-500">{errors.timeline}</p>}

        {/* Show current time */}
        <div className="mt-2 space-y-1">
          <p className="text-xs text-gray-600">
            Current time: {new Date().toLocaleTimeString('en-US', {
              hour12: true,
              hour: 'numeric',
              minute: '2-digit'
            })}
          </p>
          <p className="text-xs text-blue-600">
            📋 All timelines are available for reporting
          </p>
        </div>
      </div>

      {/* Location cascade (read-only) */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <label className="label">
            District <span className="text-red-500">*</span>
          </label>
          <input className={`input ${errors.district ? 'border-red-500' : ''}`} type="text" value={data.district} readOnly />
          {errors.district && <p className="mt-1 text-sm text-red-500">{errors.district}</p>}
          <p className="mt-1 text-xs text-gray-500">From your user profile</p>
        </div>

        <div>
          <label className="label">
            Constituency <span className="text-red-500">*</span>
          </label>
          <input className={`input ${errors.constituency ? 'border-red-500' : ''}`} type="text" value={data.constituency} readOnly />
          {errors.constituency && <p className="mt-1 text-sm text-red-500">{errors.constituency}</p>}
          <p className="mt-1 text-xs text-gray-500">From your user profile</p>
        </div>

        <div>
          <label className="label">
            Ward <span className="text-red-500">*</span>
          </label>
          <input className={`input ${errors.ward ? 'border-red-500' : ''}`} type="text" value={data.ward} readOnly />
          {errors.ward && <p className="mt-1 text-sm text-red-500">{errors.ward}</p>}
          <p className="mt-1 text-xs text-gray-500">From your user profile</p>
        </div>

        {/* Voting Center (ALWAYS a select) */}
        <div>
          <label className="label">
            Voting Center <span className="text-red-500">*</span>
          </label>

          <select
            className={`input ${errors.votingCenter ? 'border-red-500' : ''}`}
            value={hasCurrentCenter ? data.votingCenter : (centers.length ? '' : data.votingCenter || '')}
            onChange={(e) => handleInputChange('votingCenter', e.target.value)}
            disabled={centersLoading || (!centers.length && !data.votingCenter)}
          >
            {centersLoading && <option value="">Loading centers...</option>}

            {!centersLoading && !centers.length && !data.votingCenter && (
              <option value="">No centers found for this ward</option>
            )}

            {/* Preserve a pre-filled value even if it isn't in the fetched list */}
            {!hasCurrentCenter && data.votingCenter && (
              <option value={data.votingCenter}>{data.votingCenter} (pre-filled)</option>
            )}

            {!centersLoading && centers.length > 0 && (
              <>
                <option value="" disabled>Select a polling center</option>
                {centers.map((c) => (
                  <option key={c.id} value={c.name}>{c.name}</option>
                ))}
              </>
            )}
          </select>

          {centersErr && <p className="mt-1 text-sm text-red-500">{centersErr}</p>}
          {errors.votingCenter && <p className="mt-1 text-sm text-red-500">{errors.votingCenter}</p>}
          <p className="mt-1 text-xs text-gray-500">
            {data.district && data.constituency && data.ward
              ? centersLoading
                ? 'Fetching polling centers...'
                : `Loaded ${centers.length} polling center(s) for this ward`
              : 'Polling centers appear once district, constituency, and ward are set.'}
          </p>
        </div>
      </div>

      {/* Observer Information */}
      <div>
        <h3 className="mb-4 text-lg font-medium text-gray-900">Observer Information</h3>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label className="label">
              Observer Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              className={`input ${errors.observerName ? 'border-red-500' : ''}`}
              value={data.observerName}
              onChange={(e) => handleInputChange('observerName', e.target.value)}
              placeholder="Enter observer name"
            />
            {errors.observerName && <p className="mt-1 text-sm text-red-500">{errors.observerName}</p>}
          </div>

          <div>
            <label className="label">
              Observer Contact <span className="text-sm text-gray-500">(optional)</span>
            </label>
            <input
              type="tel"
              className={`input ${errors.observerContact ? 'border-red-500' : ''}`}
              placeholder="+265[7-9]xxxxxxxx (Malawi format)"
              value={data.observerContact}
              maxLength={20}
              onChange={(e) => handleInputChange('observerContact', e.target.value)}
            />
            {errors.observerContact && <p className="mt-1 text-sm text-red-500">{errors.observerContact}</p>}
            <p className="mt-1 text-xs text-gray-500">
              Format: +265[7-9] followed by 8 digits (8-20 characters)
            </p>
          </div>
        </div>

        <div className="mt-4">
          <label className="label">
            Observer Organization <span className="text-sm text-gray-500">(optional)</span>
          </label>
          <input
            type="text"
            className={`input ${errors.observerOrganization ? 'border-red-500' : ''}`}
            value={data.observerOrganization}
            onChange={(e) => handleInputChange('observerOrganization', e.target.value)}
            placeholder="Enter organization name"
          />
          {errors.observerOrganization && <p className="mt-1 text-sm text-red-500">{errors.observerOrganization}</p>}
        </div>
      </div>

      {/* Footer with Question Counter and Progress */}
      <div className="p-4 mt-8 border border-gray-100 rounded-lg bg-gray-50">
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-500">
            <span className="font-medium">{stepMetadata.totalQuestions}</span> questions in this section
          </div>
          <div className="flex items-center text-sm">
            <span className="mr-2 font-medium text-blue-600">Step {stepMetadata.stepNumber} of {stepMetadata.totalSections}</span>
            <div className="flex space-x-1">
              {Array.from({ length: stepMetadata.totalSections }, (_, i) => (
                <div
                  key={i}
                  className={`w-2.5 h-2.5 rounded-full ${i < stepMetadata.currentSection ? 'bg-blue-600' : 'bg-gray-300'}`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Step1LocationInfo;
